import _ from 'underscore';

import {
    CorvalCostOccurenceType,
    CorvalCostOccurrenceFee,
    CorvalCostOccurrenceFeeType,
    CorvalCostOccurrenceResource,
    CorvalHireType,
    CorvalProjectActivity,
    CorvalStaticProjectActivity,
    FeeBasedCostOccurrence,
    HireTypeOccurrence,
    ProjectActivityCostOccurrenceFeeItem,
    RequireField,
    Uuid
}                                         from '../types';
import { formatCurrency, sumByAttribute } from '../util';
import { TimeService }                    from './TimeService';
import { Moment }                         from 'moment';
import { debug }                          from "webpack";


let numeral = require( 'numeral' );

export enum CostDataReadMethodTypes {
    MIN,
    MAX,
    AVG,
}

export class ResourcesService {

    static PlatformCostDataReadMethod = CostDataReadMethodTypes.AVG;

    static getFormattedRangeString( min: number, max: number = null, withPrecision: boolean = false ){

        if( max ){
            return [ min, max ].map( v => formatCurrency( v, withPrecision ) ).join( ' - ' );
        }

        return formatCurrency( min, withPrecision );
    }

    static getFormattedRangeStringFromArray( values: number[], withPrecision: boolean = false ){

        return ResourcesService.getFormattedRangeString( values[ 0 ], values[ 1 ], withPrecision );
    }

    static getCostValueFromCostData( costData: number | number[], totalizationType: CostDataReadMethodTypes = ResourcesService.PlatformCostDataReadMethod ): number{

        if( _.isArray( costData ) ){

            let minCost = _.min( costData ),
                maxCost = _.max( costData );

            if( totalizationType === CostDataReadMethodTypes.MIN ){
                return minCost;
            }
            if( totalizationType === CostDataReadMethodTypes.MAX ){
                return maxCost;
            }
            if( totalizationType === CostDataReadMethodTypes.AVG ){

                return _.reduce( costData, ( sum, cost ) => sum + cost, 0 ) / 2;
            }
        }

        return costData as number;
    }

    static getYearlyCompensationFromHireType( hireType: CorvalHireType ){

        return ResourcesService.getYearlyCompensation( hireType.salary, hireType.modifier );
    }

    static getQuarterlyCompensationFromHireType( hireType: CorvalHireType ){

        return ResourcesService.getQuarterlyCompensation( hireType.salary, hireType.modifier );
    }

    static getYearlyCompensation( baseSalary: string | number, bonusModifierPrecentage: string | number ){

        let salary = numeral( baseSalary ),
            bonus  = numeral( bonusModifierPrecentage );

        return ( salary.value() + ( salary.value() * ( bonus.value() / 100 ) ) );
    }

    static getQuarterlyCompensation( baseSalary: string | number, bonusModifierPrecentage: string | number ){

        return ( ResourcesService.getYearlyCompensation( baseSalary, bonusModifierPrecentage ) / 4 );
    }

    static getTotalInitialActivityFees( projectActivity: CorvalProjectActivity, useOverrideValuesWhenPresent = true ): [ totalCost: number, professionalFee: number, operationalCost: number ]{

        let operationalCost       = numeral( projectActivity.activity.defaultOperationalCost ) ?? 0,
            professionalFees      = projectActivity.professionalFees ?? [],
            totalProfessionalFees = numeral( sumByAttribute( professionalFees, [ 'professionalFee', 'amount' ] ) );

        let hasOperationalCostOverride = !_.isNull( projectActivity.operationalCostOverride ),
            operationalCostOverride    = hasOperationalCostOverride ? numeral( projectActivity.operationalCostOverride ) : null;

        // if user defined override, return that and do not include professional fees
        if( useOverrideValuesWhenPresent && hasOperationalCostOverride ){
            return [ operationalCostOverride.value(), 0, operationalCostOverride.value(), ]
        }

        return [
            operationalCost.value() + totalProfessionalFees.value(),
            totalProfessionalFees.value(),
            operationalCost.value(),
        ]
    }

    static getTotalInitialStaticActivityFees( projectActivity: CorvalStaticProjectActivity ): [ totalCost: number, professionalFee: number, operationalCost: number, scheduledCost: number ]{

        let operationalCost       = numeral( projectActivity.operationalCost ) ?? 0,
            scheduledCosts        = projectActivity.scheduledBudgetItems ?? [],
            professionalFees      = projectActivity.professionalFees ?? [],
            totalScheduledCosts   = numeral( sumByAttribute( scheduledCosts, 'amount' ) ),
            totalProfessionalFees = numeral( sumByAttribute( professionalFees, 'amount' ) );

        return [
            operationalCost.value() + totalProfessionalFees.value() + totalScheduledCosts.value(),
            totalProfessionalFees.value(),
            operationalCost.value(),
            totalScheduledCosts.value()
        ]
    }

    static getTotalOngoingActivityCosts( projectActivity: CorvalProjectActivity, useOverrideValuesWhenPresent = true ): [ minTotalCost: number, maxTotalCost: number, quarterCount: number ]{

        let minOngoingCost = numeral( projectActivity.activity.defaultOngoingCostMin ),
            maxOngoingCost = numeral( projectActivity.activity.defaultOngoingCostMax ),
            quarters       = numeral( projectActivity.activity.defaultOngoingCostDurationQuarters );

        let hasOngoingCostOverride = !_.isNull( projectActivity.ongoingCostOverride ),
            ongoingCostOverride    = hasOngoingCostOverride ? numeral( projectActivity.ongoingCostOverride ) : null;

        // if user defined override, return that instead
        if( useOverrideValuesWhenPresent && hasOngoingCostOverride ){
            return [
                ongoingCostOverride.value(),
                ongoingCostOverride.value(),
                quarters.value()
            ]
        }

        return [
            minOngoingCost.value(),
            maxOngoingCost.value(),
            quarters.value()
        ];
    }

    static getTotalOngoingStaticActivityCosts( projectActivity: CorvalStaticProjectActivity ): [ minTotalCost: number, maxTotalCost: number, quarterCount: number ]{

        let minOngoingCost = numeral( projectActivity.ongoingCostPerQuarter ),
            maxOngoingCost = numeral( projectActivity.ongoingCostPerQuarter ),
            quarters       = numeral( projectActivity.ongoingCostQuarters );

        return [
            minOngoingCost.value(),
            maxOngoingCost.value(),
            quarters.value()
        ];
    }

    // Derive from fee-based cost occurrences which activity / custom costs start in which year.
    static getActivityCountsByYearFromCostOccurrences( feeBasedCostOccurrences: CorvalCostOccurrenceFee[] ){

        let activityCostOccurrencesByYearAggregation: Record<number, any[]> = {};

        _.chain( feeBasedCostOccurrences )
         .groupBy( ( co ) => co.itemData.id )
         .mapObject( ( occurrences ) => {

             const timestampedCostOccurrences = [ ...occurrences ].map( co => ( { ...co, costOccurrence: TimeService.ISOtoMoment( co.costOccurrence ) } ) );

             // take the earliest date only
             return _.chain( timestampedCostOccurrences )
                     .groupBy( ( co ) => co.costOccurrence.year() )
                     .mapObject( set =>
                         _.chain( set )
                          .sortBy( ( co ) => TimeService.asYearFirstDisplayDate( co.costOccurrence ) )
                          .first()
                          .value()
                     )
                     .value();

         } )
         .values()
         .map( ( coSet ) => {

             _.mapObject( coSet, ( co, year ) => {

                 if( !activityCostOccurrencesByYearAggregation[ year ] ){
                     activityCostOccurrencesByYearAggregation[ year ] = [];
                 }

                 activityCostOccurrencesByYearAggregation[ year ].push( co );
             } );
         } )
         .value();

        // Activities that start in a certain year.
        return _.mapObject(
            activityCostOccurrencesByYearAggregation,
            ( occurrences, year ) => ( {
                items: occurrences.map( co => co.itemData ),
                count: occurrences.length,
                year:  parseInt( year )
            } )
        );
    }

    // Derive from fee-based cost occurrences which activity / custom costs start in which year.
    static getHiringCountsByYearFromCostOccurrences( resourceCostOccurrences: CorvalCostOccurrenceResource[] ){

        let hiringCostOccurrencesByYearAggregation: Record<number, any[]> = {};

        _.chain( resourceCostOccurrences )
         .groupBy( co => `${ co.hireTypeId }-${ co.projectHire.id }` )
         .mapObject( ( occurrences ) => {

             const timestampedCostOccurrences = [ ...occurrences ].map( co => ( { ...co, resourceCostOccurrence: TimeService.ISOtoMoment( co.resourceCostOccurrence ) } ) );

             // take the earliest date only
             return _.chain( timestampedCostOccurrences )
                     .sortBy( ( co ) => TimeService.asYearFirstDisplayDate( co.resourceCostOccurrence ) )
                     .first()
                     .value();

         } )
         .values()
         .map( co => {

             const year = co.resourceCostOccurrence.year();

             if( !hiringCostOccurrencesByYearAggregation[ year ] ){
                 hiringCostOccurrencesByYearAggregation[ year ] = [];
             }

             hiringCostOccurrencesByYearAggregation[ year ].push( co );
         } )
         .value();

        // Activities that start in a certain year.
        return _.mapObject(
            hiringCostOccurrencesByYearAggregation,
            ( occurrences, year ) => ( {
                items: occurrences,
                count: _.reduce( occurrences, ( sum, occurrence ) => ( sum + occurrence.projectHire.headcount ), 0 ),
                year:  parseInt( year )
            } )
        );
    }

    static hasOperationalCostOverride( projectActivity: RequireField<CorvalProjectActivity, 'operationalCostOverride'> ){

        return !_.isNull( projectActivity.operationalCostOverride );
    }

    static hasOngoingCostOverride( projectActivity: RequireField<CorvalProjectActivity, 'ongoingCostOverride'> ){

        return !_.isNull( projectActivity.ongoingCostOverride );
    }

    static projectActivityRequiresResources( projectActivity: RequireField<CorvalProjectActivity | CorvalStaticProjectActivity, 'requiredHires'> ){

        return ( projectActivity?.requiredHires?.length > 0 );
    }

    static isProjectActivityFullyResourced( projectActivity: RequireField<CorvalProjectActivity | CorvalStaticProjectActivity, 'requiredHires'> ){

        /*
         requiredHireTypes : [ActivityHireType] {
         hireType { ... }                             // description of hire type
         headcount                                    // how many needed
         isSatisfied(projectActvityId) : bool         // is this PA satisfied in what it needs to be covered and avoid professional fees
         hires(projectActvityId) : [ProjectHire]      // the actual hires made so far for this particular hire
         }
         */

        let hasRequiredHireTypesAttr = !!( projectActivity?.requiredHires ),
            hasNeededHires           = hasRequiredHireTypesAttr && ( projectActivity?.requiredHires?.length > 0 ),
            hasAllRequiredHires      = false;

        if( hasNeededHires ){

            let requiredHireTypes = projectActivity.requiredHires;

            hasAllRequiredHires = ( _.chain( requiredHireTypes ).pluck( 'isSatisfied' ).compact().value().length === requiredHireTypes.length );
        }

        return hasAllRequiredHires;
    }

    static isCustomDataCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return ( costOccurrence.instanceType === CorvalCostOccurenceType.Customuser )
    }

    static isNotCustomDataCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return !ResourcesService.isCustomDataCostOccurrence( costOccurrence );
    }

    static isCustomProjectActivityCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return ( costOccurrence.instanceType === CorvalCostOccurenceType.Customprojectactivity )
    }

    static isRecurringProjectActivityCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) &&
            ( ( costOccurrence as ProjectActivityCostOccurrenceFeeItem )?.itemData?.isRecurrence ?? false )
        );
    }

    static isCostOccurrenceWithinTimeframe( costOccurrence: FeeBasedCostOccurrence, currentStartDate: Moment | string, currentEndDate: Moment | string ){

        const coStart = TimeService.ISOtoMoment( costOccurrence.costOccurrence ),
              coEnd   = TimeService.ISOtoMoment( costOccurrence.costOccurrence ).endOf( 'quarter' );

        return TimeService.timerangeOverlapsTimeframe( coStart, coEnd, currentStartDate, currentEndDate );
    }

    static isHireOccurrenceWithinTimeframe( costOccurrence: HireTypeOccurrence, currentStartDate: Moment | string, currentEndDate: Moment | string ){

        const coStart = TimeService.ISOtoMoment( costOccurrence.resourceCostOccurrence ),
              coEnd   = TimeService.ISOtoMoment( costOccurrence.resourceCostOccurrence ).endOf( 'quarter' );

        return TimeService.timerangeOverlapsTimeframe( coStart, coEnd, currentStartDate, currentEndDate );
    }

    static costOccurrenceMatchesProjectActivityId( activityId: Uuid, costOccurrence: FeeBasedCostOccurrence ){

        return (
            ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) &&
            ( ( ( costOccurrence as ProjectActivityCostOccurrenceFeeItem )?.itemData?.id ?? null ) === activityId )
        );
    }

    static isProfessionalFeeCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ( ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) ) &&
            ( costOccurrence.costAmountType === CorvalCostOccurrenceFeeType.Professionalfee )
        );
    }

    static isOperationalFeeCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ( ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) ) &&
            ( costOccurrence.costAmountType === CorvalCostOccurrenceFeeType.Operationalcost )
        );
    }

    static isScheduledFeeCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ( ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) ) &&
            ( costOccurrence.costAmountType === CorvalCostOccurrenceFeeType.Scheduledbudgetitem )
        );
    }

    static isOngoingCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ( ResourcesService.isNotCustomDataCostOccurrence( costOccurrence ) ) &&
            ( costOccurrence.costAmountType === CorvalCostOccurrenceFeeType.Ongoingcost )
        );
    }

    static isInitialCostOccurrence( costOccurrence: FeeBasedCostOccurrence ){

        return (
            ResourcesService.isOperationalFeeCostOccurrence( costOccurrence ) ||
            ResourcesService.isProfessionalFeeCostOccurrence( costOccurrence )
        );
    }
}
